import { useMemo } from "react";
import { GroupChannel } from "@sendbird/chat/groupChannel";
import useSendbirdStateContext from "@sendbird/uikit-react/useSendbirdStateContext";
import { useLowEngagementTeens } from "@/lib/hooks/mentors/use-low-engagement-teens";
import { useUnreadCounts } from "@/lib/api/shareable-entities";
import { useGetMyMentorships } from "@/lib/api/mentorships";

export interface TeenChannelCategories {
  needsResponse: GroupChannel[];
  online: GroupChannel[];
  lowEngagement: GroupChannel[];
  newTeens: GroupChannel[];
  allTeens: GroupChannel[];
  supervisor: GroupChannel[];
  loading: boolean;
}

export function useTeenChannelCategories(
  channels: GroupChannel[],
): TeenChannelCategories {
  const store = useSendbirdStateContext();
  const myUserId = store.config.userId;

  const { data: lowEngagementTeens = [], isLoading: lowEngagementLoading } =
    useLowEngagementTeens();
  const { data: unreadNoteCounts = {} } = useUnreadCounts();
  const { data: myMentorships = [] } = useGetMyMentorships();

  const lowEngagementUrls = useMemo(
    () => new Set(lowEngagementTeens.map((teen) => teen.sdkUrl)),
    [lowEngagementTeens],
  );

  const menteeIdsWithUnreadNotes = useMemo(
    () =>
      new Set(
        Object.entries(unreadNoteCounts)
          .filter(([, count]) => count > 0)
          .map(([menteeId]) => menteeId),
      ),
    [unreadNoteCounts],
  );

  // Reactivated teens stay in "Needs response" until the mentor replies.
  const menteeIdsAwaitingReactivationResponse = useMemo(
    () =>
      new Set(
        myMentorships
          .filter((m) => {
            const reactivation = m.metadata?.reactivation;
            return !!reactivation && reactivation.banner_dismissed !== true;
          })
          .map((m) => m.mentee?.id)
          .filter((id): id is string => !!id),
      ),
    [myMentorships],
  );

  return useMemo(() => {
    const supervisorChannels = channels.filter(
      (c) => c.customType === "SUPERVISION",
    );
    const mentorshipChannels = channels.filter(
      (c) => c.customType === "MENTORSHIP",
    );

    const needsResponse: GroupChannel[] = [];
    const newTeens: GroupChannel[] = [];

    mentorshipChannels.forEach((channel) => {
      const others = channel.members.filter((m) => m.userId !== myUserId);
      const menteeId = others?.[0]?.userId;

      const lastMsg = channel.lastMessage as
        | {
            isAdminMessage?: () => boolean;
            sender?: { userId: string };
          }
        | null
        | undefined;
      const isAdminMessage =
        typeof lastMsg?.isAdminMessage === "function"
          ? lastMsg.isAdminMessage()
          : !lastMsg?.sender;
      const lastFromOther =
        !isAdminMessage && lastMsg?.sender?.userId !== myUserId;

      if (
        lastFromOther ||
        channel.unreadMessageCount > 0 ||
        (menteeId && menteeIdsWithUnreadNotes.has(menteeId)) ||
        (menteeId && menteeIdsAwaitingReactivationResponse.has(menteeId))
      ) {
        needsResponse.push(channel);
      }

      const ageDays =
        (Date.now() - new Date(channel.createdAt).getTime()) /
        (1000 * 60 * 60 * 24);
      if (ageDays < 7) newTeens.push(channel);
    });

    const online = mentorshipChannels.filter((c) => {
      const other = c.members.find((m) => m.userId !== myUserId);
      return other?.connectionStatus === "online";
    });

    const lowEngagement = mentorshipChannels.filter((c) =>
      lowEngagementUrls.has(c.url),
    );

    return {
      needsResponse,
      online,
      lowEngagement,
      newTeens,
      allTeens: mentorshipChannels,
      supervisor: supervisorChannels,
      loading: lowEngagementLoading,
    };
  }, [
    channels,
    myUserId,
    lowEngagementUrls,
    menteeIdsWithUnreadNotes,
    menteeIdsAwaitingReactivationResponse,
    lowEngagementLoading,
  ]);
}
